import React, {useEffect, useState} from 'react';
import {Box, Button, Grid, Paper} from '@mui/material';
import ConfirmDialog from '../ConfirmDialog';
import ErrorBoundary from '../ErrorBoundary';

export enum FormEnum {
    'add',
    'edit',
}

export interface FormI {
    singularName: string;
    newPayload: any;
    data: any;
    mode?: FormEnum;

    onCancel?: any;
    onReject?: any;
    onDelete?: any;
    onApprove?: any;
    onAdd?: any;
    onEdit?: any;
    validate?: any;

    approveAllowed?: boolean;
    editAllowed: boolean;
    deleteAllowed: boolean;
    addAllowed: boolean;
    rejectAllowed?: boolean;

    ReadOnlyComponent: React.FC<{ data: any }>;
    EditFormComponent: React.FC<{ data: any; setPayload: any }>;
}

function FormContainer({
                           singularName,
                           newPayload,
                           data,
                           mode,

                           onCancel,
                           onApprove,
                           onAdd,
                           onDelete,
                           onReject,
                           onEdit,
                           validate,

                           approveAllowed,
                           editAllowed,
                           addAllowed,
                           deleteAllowed,
                           rejectAllowed,

                           ReadOnlyComponent,
                           EditFormComponent,
                       }: FormI) {
    const [payload, setPayload] = useState<any>(data || newPayload);
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [confirmReject, setConfirmReject] = useState<boolean>(false);
    const [isValid, setValid] = useState<boolean>(true);

    const scrollToCrudTitle = () => {
        (document as any).querySelector('#crud-title').scrollIntoView({
            behavior: 'smooth',
        });
    };

    const cancelPress = () => {
        scrollToCrudTitle();
        if (onCancel) onCancel();
    };
    const addPress = () => {
        scrollToCrudTitle();
        if (onAdd) onAdd(payload);
    };
    const approvePress = () => {
        scrollToCrudTitle();
        if (onApprove) onApprove(payload);
    };
    const deletePress = () => {
        scrollToCrudTitle();
        if (onDelete) onDelete(payload._id);
    };
    const rejectPress = () => {
        scrollToCrudTitle();
        if (onReject) onReject(payload._id);
    };
    const editPress = () => {
        scrollToCrudTitle();
        if (onEdit) onEdit(payload);
    };

    useEffect(() => {
        if (validate) {
            const [valid, errorsList] = validate(payload);
            setValid(valid);
        }
    }, [payload]);

    return (
        <>
            <ConfirmDialog
                id='Companys-delete'
                open={confirmDelete}
                question='Delete'
                questionDescription={`Are you sure you want to delete this ${singularName}?`}
                primaryButtonLabel='Delete'
                primaryAction={() => {
                    deletePress();
                    setConfirmDelete(false);
                }}
                secondaryButtonLabel='Cancel'
                secondaryAction={() => {
                    setConfirmDelete(false);
                }}
            />
            <ConfirmDialog
                id='Companys-reject'
                open={confirmReject}
                question='Reject'
                questionDescription={`Are you sure you want to reject this ${singularName}?`}
                primaryButtonLabel='Reject'
                primaryAction={() => {
                    rejectPress();
                    setConfirmReject(false);
                }}
                secondaryButtonLabel='Cancel'
                secondaryAction={() => {
                    setConfirmReject(false);
                }}
            />
            <Paper sx={{padding: 3}} elevation={2}>
                <ErrorBoundary>
                    <Grid
                        id='form-component'
                        container
                        sx={{display: 'flex', flexDirection: 'column'}}
                    >
                        <Grid item xs={12}>
                            <Box
                                component='form'
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    marginTop: 3,
                                    marginBottom: 5,
                                }}
                            >
                                {!editAllowed && !addAllowed ? (
                                    <ReadOnlyComponent data={data}/>
                                ) : (
                                    <EditFormComponent data={payload} setPayload={setPayload}/>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', gap: 2}}>
                            <Button variant='outlined' onClick={cancelPress} tabIndex={-1}>
                                Cancel
                            </Button>
                            <Box sx={{flex: 1}}></Box>
                            {rejectAllowed && mode === FormEnum.edit && (
                                <Button
                                    variant='contained'
                                    color='error'
                                    onClick={() => setConfirmReject(true)}
                                >
                                    Reject
                                </Button>
                            )}
                            {deleteAllowed && mode === FormEnum.edit && (
                                <Button
                                    variant='contained'
                                    color='error'
                                    onClick={() => setConfirmDelete(true)}
                                >
                                    Delete
                                </Button>
                            )}
                            {editAllowed && mode === FormEnum.edit && (
                                <Button
                                    variant='contained'
                                    onClick={editPress}
                                    disabled={!isValid}
                                >
                                    Save
                                </Button>
                            )}
                            {addAllowed && mode === FormEnum.add && (
                                <Button
                                    variant='contained'
                                    color='success'
                                    onClick={addPress}
                                    disabled={!isValid}
                                >
                                    Add
                                </Button>
                            )}
                            {approveAllowed && mode === FormEnum.edit && (
                                <Button
                                    variant='contained'
                                    color='success'
                                    onClick={approvePress}
                                    disabled={!isValid}
                                >
                                    Approve
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </ErrorBoundary>
            </Paper>
        </>
    );
}

export default FormContainer;
